/**
 * Audio analysis utilities using AudioFlux features
 * Converts technical features into artist-friendly insights
 */

export interface AudioQualityIssues {
  issues: Array<{
    type: 'warning' | 'error' | 'info';
    message: string;
    severity: 'low' | 'medium' | 'high';
  }>;
  overallQuality: 'excellent' | 'good' | 'fair' | 'poor';
}

export interface MixingRecommendation {
  type: 'eq' | 'compression' | 'saturation' | 'reverb' | 'panning' | 'volume';
  message: string;
  severity: 'low' | 'medium' | 'high';
  priority: number;
}

export interface GenrePrediction {
  genre: string;
  confidence: number;
  reasoning: string;
}

/**
 * Analyze audio quality based on AudioFlux features
 */
export function analyzeAudioQuality(audiofluxFeatures: any): AudioQualityIssues {
  const issues: AudioQualityIssues['issues'] = [];
  
  if (!audiofluxFeatures) {
    return {
      issues: [],
      overallQuality: 'good'
    };
  }

  // Check spectral centroid (brightness)
  const centroid = audiofluxFeatures.spectral_centroid_mean || 0;
  if (centroid < 1000) {
    issues.push({
      type: 'warning',
      message: 'Audio may sound muffled or lack high-frequency clarity',
      severity: 'medium'
    });
  } else if (centroid > 5000) {
    issues.push({
      type: 'info',
      message: 'Very bright mix - may benefit from slight high-end rolloff',
      severity: 'low'
    });
  }

  // Check spectral flatness (noisiness)
  const flatness = audiofluxFeatures.spectral_flatness_mean || 0;
  if (flatness > 0.7) {
    issues.push({
      type: 'warning',
      message: 'High noise level detected - consider noise reduction',
      severity: 'medium'
    });
  }

  // Check zero crossing rate (clipping/distortion)
  const zcr = audiofluxFeatures.zcr_mean || 0;
  if (zcr > 0.4) {
    issues.push({
      type: 'error',
      message: 'Possible clipping or distortion detected',
      severity: 'high'
    });
  } else if (zcr < 0.05) {
    issues.push({
      type: 'info',
      message: 'Very clean signal - minimal distortion',
      severity: 'low'
    });
  }

  // Check spectral bandwidth (frequency spread)
  const bandwidth = audiofluxFeatures.spectral_bandwidth_mean || 0;
  if (bandwidth < 500 && centroid > 0) {
    issues.push({
      type: 'warning',
      message: 'Narrow frequency range - track may lack depth',
      severity: 'medium'
    });
  }

  // Determine overall quality
  const errorCount = issues.filter(i => i.type === 'error').length;
  const warningCount = issues.filter(i => i.type === 'warning').length;
  
  let overallQuality: AudioQualityIssues['overallQuality'] = 'excellent';
  if (errorCount > 0) {
    overallQuality = 'poor';
  } else if (warningCount >= 2) {
    overallQuality = 'fair';
  } else if (warningCount === 1) {
    overallQuality = 'good';
  }

  return {
    issues,
    overallQuality
  };
}

/**
 * Generate mixing recommendations based on AudioFlux features
 */
export function getMixingRecommendations(audiofluxFeatures: any): MixingRecommendation[] {
  const recommendations: MixingRecommendation[] = [];
  
  if (!audiofluxFeatures) {
    return [];
  }

  const centroid = audiofluxFeatures.spectral_centroid_mean || 0;
  const bandwidth = audiofluxFeatures.spectral_bandwidth_mean || 0;
  const flatness = audiofluxFeatures.spectral_flatness_mean || 0;
  const flux = audiofluxFeatures.spectral_flux_mean || 0;
  const rolloff = audiofluxFeatures.spectral_rolloff_mean || 0;

  // EQ Recommendations
  if (centroid < 2000) {
    recommendations.push({
      type: 'eq',
      message: 'Boost high frequencies (2-8kHz) for clarity and presence',
      severity: 'medium',
      priority: 1
    });
  } else if (centroid > 4000) {
    recommendations.push({
      type: 'eq',
      message: 'Consider slight high-end rolloff to reduce harshness',
      severity: 'low',
      priority: 3
    });
  }

  if (centroid > 0 && centroid < 1500 && bandwidth < 1000) {
    recommendations.push({
      type: 'eq',
      message: 'Add presence in the 1-3kHz range for vocal/instrument clarity',
      severity: 'medium',
      priority: 2
    });
  }

  // Compression/Dynamic Range
  if (flux < 0.1) {
    recommendations.push({
      type: 'compression',
      message: 'Low dynamic variation - consider adding subtle compression for movement',
      severity: 'low',
      priority: 4
    });
  } else if (flux > 0.5) {
    recommendations.push({
      type: 'compression',
      message: 'High dynamic variation - consider gentle compression to control peaks',
      severity: 'medium',
      priority: 2
    });
  }

  // Saturation/Warmth
  if (centroid < 2500 && flatness < 0.5) {
    recommendations.push({
      type: 'saturation',
      message: 'Add subtle saturation or harmonic enhancement for warmth',
      severity: 'low',
      priority: 3
    });
  }

  // Reverb/Space
  if (bandwidth < 800) {
    recommendations.push({
      type: 'reverb',
      message: 'Add subtle reverb or delay to create more spatial depth',
      severity: 'low',
      priority: 4
    });
  }

  // Sort by priority
  return recommendations.sort((a, b) => a.priority - b.priority);
}

/**
 * Predict genre based primarily on BPM, with spectral features as secondary indicators
 */
export function predictGenre(audiofluxFeatures: any, tempo?: number): GenrePrediction[] {
  const predictions: GenrePrediction[] = [];
  
  if (!audiofluxFeatures) {
    return [{
      genre: 'Unknown',
      confidence: 0,
      reasoning: 'Insufficient audio data'
    }];
  }

  const centroid = audiofluxFeatures.spectral_centroid_mean || 0;
  const bandwidth = audiofluxFeatures.spectral_bandwidth_mean || 0;
  const flux = audiofluxFeatures.spectral_flux_mean || 0;
  const zcr = audiofluxFeatures.zcr_mean || 0;
  const rolloff = audiofluxFeatures.spectral_rolloff_mean || 0;

  // Genre classification logic - BPM is the strongest indicator
  const features = {
    bright: centroid > 3000,
    dark: centroid < 2000,
    wide: bandwidth > 2000,
    narrow: bandwidth < 1000,
    dynamic: flux > 0.3,
    static: flux < 0.1,
    clean: zcr < 0.15,
    distorted: zcr > 0.3,
    highRolloff: rolloff > 8000,
    lowRolloff: rolloff < 4000
  };

  // PRIMARY: BPM-based classification (BPM is the strongest genre indicator)
  if (tempo && tempo > 0) {
    const roundedTempo = Math.round(tempo);
    
    // Very fast / Hardcore / Speedcore (above 180 BPM)
    if (tempo > 180) {
      predictions.push({
        genre: 'Hardcore / Speedcore',
        confidence: 0.95,
        reasoning: `Extremely fast tempo (${roundedTempo} BPM) is the primary indicator of hardcore or speedcore electronic music`
      });
    }
    
    // Drum & Bass / Hardcore (140-180 BPM)
    else if (tempo >= 140 && tempo <= 180) {
      predictions.push({
        genre: 'Drum & Bass / Hardcore',
        confidence: 0.90,
        reasoning: `Extremely fast tempo (${roundedTempo} BPM) is the primary indicator of drum & bass or hardcore electronic music`
      });
    }
    
    // Techno / Trance (130-140 BPM)
    else if (tempo >= 130 && tempo < 140) {
      predictions.push({
        genre: 'Techno / Trance',
        confidence: 0.90,
        reasoning: `Very fast tempo (${roundedTempo} BPM) is the primary indicator of techno or trance music`
      });
    }
    
    // House / EDM (120-130 BPM)
    else if (tempo >= 120 && tempo < 130) {
      predictions.push({
        genre: 'House / EDM',
        confidence: 0.90,
        reasoning: `Fast tempo (${roundedTempo} BPM) is the primary indicator of house or electronic dance music`
      });
    }
    
    // Pop / Dance Pop (100-120 BPM)
    else if (tempo >= 100 && tempo < 120) {
      predictions.push({
        genre: 'Pop / Dance Pop',
        confidence: 0.85,
        reasoning: `Upbeat tempo (${roundedTempo} BPM) is the primary indicator of pop or dance pop music`
      });
    }
    
    // Hip-Hop / Trap (70-100 BPM)
    else if (tempo >= 70 && tempo < 100) {
      predictions.push({
        genre: 'Hip-Hop / Trap',
        confidence: 0.90,
        reasoning: `Moderate tempo (${roundedTempo} BPM) is the primary indicator of hip-hop or trap music`
      });
    }
    
    // Ambient / Chill / Downtempo (60-90 BPM)
    else if (tempo >= 60 && tempo < 90) {
      predictions.push({
        genre: 'Ambient / Chill',
        confidence: 0.90,
        reasoning: `Slow tempo (${roundedTempo} BPM) is the primary indicator of ambient or chill music`
      });
    }
    
    // Very slow / Ballad (below 60 BPM)
    else if (tempo < 60) {
      predictions.push({
        genre: 'Ballad / Very Slow',
        confidence: 0.85,
        reasoning: `Very slow tempo (${roundedTempo} BPM) is the primary indicator of ballad or very slow tempo music`
      });
    }
    
    // Add Rock/Metal as alternative if spectral features strongly suggest it
    if (features.distorted && features.wide && features.dynamic && tempo >= 80 && tempo <= 140) {
      predictions.push({
        genre: 'Rock / Metal',
        confidence: 0.65,
        reasoning: `Moderate tempo (${roundedTempo} BPM) with high distortion suggests rock or metal as alternative`
      });
    }
  }

  // FALLBACK: Spectral-based classification (only when BPM is unknown)
  if (!tempo || tempo <= 0 || predictions.length === 0) {
    // Rock/Metal (distortion is key indicator)
    if (features.distorted && features.wide && features.dynamic) {
      predictions.push({
        genre: 'Rock / Metal',
        confidence: 0.70,
        reasoning: 'High distortion with wide frequency range and dynamic energy'
      });
    }
    
    // Electronic/Dance (bright and wide)
    else if (features.bright && features.wide && features.dynamic) {
      predictions.push({
        genre: 'Electronic / Dance',
        confidence: 0.65,
        reasoning: 'Bright, wide frequency spectrum with high energy'
      });
    }
    
    // Ambient/Chill (dark and static - ONLY if slow tempo or no tempo)
    else if (features.dark && features.static && features.lowRolloff && (!tempo || tempo < 90)) {
      predictions.push({
        genre: 'Ambient / Chill',
        confidence: 0.70,
        reasoning: 'Dark, minimal dynamics with smooth frequency response'
      });
    }
    
    // Pop (bright and clean)
    else if (features.bright && features.clean && features.dynamic) {
      predictions.push({
        genre: 'Pop',
        confidence: 0.65,
        reasoning: 'Bright, clean sound with dynamic energy'
      });
    }
    
    // Jazz/Blues (clean and wide, no distortion)
    else if (features.clean && features.wide && !features.distorted) {
      predictions.push({
        genre: 'Jazz / Blues',
        confidence: 0.60,
        reasoning: 'Clean, wide frequency spectrum with natural dynamics'
      });
    }
  }

  // If no strong matches, provide general classification
  if (predictions.length === 0) {
    if (features.bright) {
      predictions.push({
        genre: 'Bright / Upbeat',
        confidence: 0.50,
        reasoning: 'High-frequency content suggests energetic, bright music'
      });
    } else if (features.dark) {
      predictions.push({
        genre: 'Dark / Atmospheric',
        confidence: 0.50,
        reasoning: 'Low-frequency focus suggests moody, atmospheric music'
      });
    } else {
      predictions.push({
        genre: 'Balanced',
        confidence: 0.40,
        reasoning: 'Balanced frequency spectrum'
      });
    }
  }

  // Sort by confidence
  return predictions.sort((a, b) => b.confidence - a.confidence);
}
